import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  ScanFace,
  Camera,
  Edit2,
  Trash2,
  Check,
  X,
  Loader2,
  ChevronDown,
  Eye,
} from 'lucide-react';

import { usePerson, usePersonPhotos, useUpdatePerson, useDeletePerson } from '@/api/hooks/useFaces';
import type { PersonRole } from '@/api/types/face.types';

const ROLE_OPTIONS: PersonRole[] = [
  'bride',
  'groom',
  'family',
  'guest',
  'vendor',
  'unknown',
];

export function PersonDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: person, isLoading: personLoading } = usePerson(id || '');
  const { data: photosData, isLoading: photosLoading } = usePersonPhotos(id || '');
  const updatePerson = useUpdatePerson();
  const deletePerson = useDeletePerson();

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState<PersonRole>('unknown');

  const startEdit = () => {
    setEditName(person?.name || '');
    setEditRole(person?.role || 'unknown');
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
  };

  const saveEdit = () => {
    if (!id) return;
    updatePerson.mutate(
      { id, payload: { name: editName.trim(), role: editRole } },
      { onSuccess: () => setIsEditing(false) },
    );
  };

  const handleDelete = () => {
    if (!id) return;
    if (window.confirm(t('faces.deletePersonConfirm'))) {
      deletePerson.mutate(id, {
        onSuccess: () => navigate('/people'),
      });
    }
  };

  if (personLoading) {
    return (
      <div className="page-container flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!person) {
    return (
      <div className="page-container">
        <p className="text-sm text-gray-500">{t('status.noData')}</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <Link
          to="/people"
          className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="page-title">
          {person.name || t('faces.unknownPerson')}
        </h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Sidebar: Person info */}
        <div className="card">
          {/* Avatar */}
          <div className="mb-4 flex justify-center">
            {person.representative_face_url ? (
              <img
                src={person.representative_face_url}
                alt={person.name || t('faces.unknownPerson')}
                className="h-24 w-24 rounded-full object-cover ring-2 ring-white shadow-lg dark:ring-gray-800"
              />
            ) : (
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                <User className="h-12 w-12 text-gray-400" />
              </div>
            )}
          </div>

          {/* Name & Role */}
          {isEditing ? (
            <div className="space-y-3">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder={t('faces.namePlaceholder')}
                className="input-field w-full text-sm"
                autoFocus
              />
              <div className="relative">
                <select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value as PersonRole)}
                  className="input-field w-full appearance-none pe-8 text-sm"
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {t(`faces.roles.${r}`)}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute end-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={saveEdit}
                  disabled={updatePerson.isPending}
                  className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  <Check className="h-3 w-3" />
                  {t('actions.save')}
                </button>
                <button
                  onClick={cancelEdit}
                  className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
                >
                  <X className="h-3 w-3" />
                  {t('actions.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                {person.name || t('faces.unknownPerson')}
              </h3>
              {person.role && person.role !== 'unknown' && (
                <span className="mt-1 inline-block rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
                  {t(`faces.roles.${person.role}`)}
                </span>
              )}
            </div>
          )}

          {/* Stats */}
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <ScanFace className="h-4 w-4" />
                {t('faces.totalFaces')}
              </span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {person.face_count}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <Camera className="h-4 w-4" />
                {t('faces.totalPhotos')}
              </span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {person.photo_count}
              </span>
            </div>
          </div>

          {/* Actions */}
          {!isEditing && (
            <div className="mt-4 flex gap-2">
              <button
                onClick={startEdit}
                className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-gray-100 px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <Edit2 className="h-3 w-3" />
                {t('actions.edit')}
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center justify-center gap-1 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700 transition-colors hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          )}

          {/* Face gallery */}
          {person.face_detections && person.face_detections.length > 0 && (
            <div className="mt-6">
              <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {t('faces.faceGallery')}
              </h4>
              <div className="grid grid-cols-4 gap-1.5">
                {person.face_detections.map((face) => (
                  <div key={face.id} className="relative aspect-square overflow-hidden rounded-md">
                    {face.crop_url ? (
                      <img
                        src={face.crop_url}
                        alt="Face"
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gray-100 dark:bg-gray-700">
                        <User className="h-4 w-4 text-gray-400" />
                      </div>
                    )}
                    {face.eyes_open === false && (
                      <div className="absolute bottom-0 end-0 rounded-tl bg-amber-500/80 p-0.5">
                        <Eye className="h-2.5 w-2.5 text-white" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Main: Photos */}
        <div className="card lg:col-span-3">
          <h3 className="mb-4 font-semibold text-gray-900 dark:text-gray-100">
            {t('faces.photosOfPerson', {
              name: person.name || t('faces.unknownPerson'),
            })}
          </h3>

          {photosLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
            </div>
          ) : photosData && photosData.results.length > 0 ? (
            <>
              <p className="mb-4 text-xs text-gray-500">
                {t('faces.photosCount', { count: photosData.count })}
              </p>
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
                {photosData.results.map((photo) => (
                  <Link
                    key={photo.id}
                    to={`/photos/${photo.id}`}
                    className="group relative aspect-square overflow-hidden rounded-lg"
                  >
                    <img
                      src={
                        photo.thumbnail_medium ||
                        photo.thumbnail_small ||
                        `/api/v1/photos/${photo.id}/serve_thumbnail/?size=small`
                      }
                      alt={photo.original_filename}
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      loading="lazy"
                    />
                    {photo.quality_score != null && (
                      <div className="absolute bottom-1 end-1 rounded bg-black/60 px-1.5 py-0.5 text-xs font-medium text-white">
                        {Math.round(photo.quality_score)}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('status.noData')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
